import React from 'react';
import { RotateCcwIcon } from 'lucide-react';
import { Chip } from '../ui/Chip';
import { Field, Input, Switch } from '../ui/Field';
import { Button } from '../ui/Button';
import { cuisines, dietaryCapabilities } from '../../data/taxonomy';
import { currency } from '../../utils/format';
import { useLocale } from '../../contexts/LocaleContext';
import { defaultFilters } from '../../utils/search';
import type { Filters } from '../../utils/search';
import { cn } from '../../utils/cn';
function Group({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <section className="border-b border-hairline py-4 first:pt-0 last:border-0" data-id="element-441" data-mp="gjqiwd3qozfb">
      <h3 className="mb-2.5 text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-442" data-mp="glukupi2vocy">{label}</h3>
      {children}
    </section>;
}
export function FilterPanel({
  filters,
  onChange,
  onReset,
  className
}: {
  filters: Filters;
  onChange: (next: Filters) => void;
  onReset: () => void;
  className?: string;
}) {
  const {
    locale,
    currencyCode
  } = useLocale();
  const set = <K extends keyof Filters,>(key: K, value: Filters[K]) => onChange({
    ...filters,
    [key]: value
  });
  const toggleIn = (key: 'cuisines' | 'dietary', value: string) => {
    const list = filters[key];
    set(key, list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
  };
  return <div className={cn('flex flex-col', className)} data-id="element-443" data-mp="1w9qm6yoblp93">
      <Group label="Service" data-id="element-444" data-mp="2ttupgse0u21f">
        <div className="flex gap-1.5 rounded-full bg-oat p-1" role="radiogroup" aria-label="Service type" data-id="element-445" data-mp="2tx8rfzirw98c">
          {([{
          id: 'any',
          label: 'Either'
        }, {
          id: 'in_home',
          label: 'At mine'
        }, {
          id: 'delivery',
          label: 'Delivered'
        }] as const).map(option => <button key={option.id} type="button" role="radio" aria-checked={filters.fulfillment === option.id} onClick={() => set('fulfillment', option.id)} className={cn('flex-1 rounded-full px-2 py-1.5 text-[12px] font-semibold transition-colors duration-150 ease-out', filters.fulfillment === option.id ? 'bg-surface text-ink shadow-lift' : 'text-ink-2 hover:text-ink')} data-id="element-446" data-mp="3cm5ouc9gvhus">
              {option.label}
            </button>)}
        </div>
      </Group>

      <Group label="Occasion" data-id="element-447" data-mp="m94l4tedgdi0">
        <div className="grid grid-cols-2 gap-2" data-id="element-448" data-mp="m82zjuzqc8m1">
          <Field label="Date" id="f-date" data-id="element-449" data-mp="r90fs216iskw">
            <Input id="f-date" type="date" value={filters.when} onChange={e => set('when', e.target.value)} data-id="element-450" data-mp="ra23p73an9k1" />
          </Field>
          <Field label="Guests" id="f-guests" data-id="element-451" data-mp="db2gm3vfj2pz">
            <Input id="f-guests" type="number" min={1} max={40} value={filters.guests} onChange={e => set('guests', Number(e.target.value) || 1)} className="tnum" data-id="element-452" data-mp="i8u4mmsye4ow" />
          </Field>
        </div>
      </Group>

      <Group label="Cuisine" data-id="element-453" data-mp="2mqa4dlhuj25s">
        <div className="flex flex-wrap gap-1.5" data-id="element-454" data-mp="2mnecadsoj7p9">
          {cuisines.slice(0, 10).map(cuisine => <Chip key={cuisine.id} active={filters.cuisines.includes(cuisine.id)} onClick={() => toggleIn('cuisines', cuisine.id)} data-id="element-455" data-mp="2vi2etw6vebad">
              {cuisine.label}
            </Chip>)}
        </div>
      </Group>

      <Group label="Price" data-id="element-456" data-mp="35fqjbdua2x5q">
        <label htmlFor="f-price" className="flex items-baseline justify-between text-sm" data-id="element-457" data-mp="35eync1wrqxex">
          <span className="text-ink-2" data-id="element-458" data-mp="30g3yai87kcmd">Up to</span>
          <span className="tnum font-semibold text-ink" data-id="element-459" data-mp="30jjfprpctxqy">
            {currency(filters.maxPricePerGuest, locale, currencyCode)} / guest
          </span>
        </label>
        <input id="f-price" type="range" min={400} max={6000} step={100} value={filters.maxPricePerGuest} onChange={e => set('maxPricePerGuest', Number(e.target.value))} className="mt-2 w-full accent-primary" data-id="element-460" data-mp="3afuucocf2zok" />
        <p className="mt-1 text-[12px] text-ink-3" data-id="element-461" data-mp="1hi7mqtt9nfrf">Compared on each chef’s lowest per-guest menu.</p>
      </Group>

      <Group label="Rating" data-id="element-462" data-mp="1mjolojs6ws5d">
        <div className="flex flex-wrap gap-1.5" data-id="element-463" data-mp="1qjfa4gu8uw6q">
          {[0, 4.5, 4.8].map(value => <Chip key={value} active={filters.minRating === value} onClick={() => set('minRating', value)} data-id="element-464" data-mp="1vi0acg9hxfv6">
              {value === 0 ? 'Any' : `${value}+`}
            </Chip>)}
        </div>
      </Group>

      <Group label="Distance" data-id="element-465" data-mp="12t3090ffupdi">
        <label htmlFor="f-distance" className="flex items-baseline justify-between text-sm" data-id="element-466" data-mp="12u4etevnxtzz">
          <span className="text-ink-2" data-id="element-467" data-mp="17rw8adbyho6a">Within</span>
          <span className="tnum font-semibold text-ink" data-id="element-468" data-mp="1cj4g54xn11hc">{filters.maxDistance} km</span>
        </label>
        <input id="f-distance" type="range" min={2} max={30} step={1} value={filters.maxDistance} onChange={e => set('maxDistance', Number(e.target.value))} className="mt-2 w-full accent-primary" data-id="element-469" data-mp="f4a2qzj649gs" />
      </Group>

      <Group label="Dietary capability" data-id="element-470" data-mp="7kr4xn59mbo5">
        <div className="flex flex-wrap gap-1.5" data-id="element-471" data-mp="7lsnecom20fw">
          {dietaryCapabilities.slice(0, 5).map(cap => <Chip key={cap} active={filters.dietary.includes(cap)} onClick={() => toggleIn('dietary', cap)} data-id="element-472" data-mp="2jl8be8hi49c">
              {cap}
            </Chip>)}
        </div>
      </Group>

      <Group label="Trust" data-id="element-473" data-mp="3pqvws9lm0ym4">
        <Switch checked={filters.verifiedOnly} onChange={next => set('verifiedOnly', next)} label="Fully verified only" description="ID, hygiene, insurance and background all confirmed." data-id="element-474" data-mp="3ps7068b584cp" />
        <Switch checked={filters.instantOnly} onChange={next => set('instantOnly', next)} label="Instant book" description="Skip the quote wait on published dates." data-id="element-475" data-mp="3b1pzwsa8vkhe" />
      </Group>

      <div className="pt-4" data-id="element-476" data-mp="3211o848bpw7o">
        <Button variant="subtle" size="sm" full onClick={onReset} disabled={JSON.stringify(filters) === JSON.stringify(defaultFilters)} data-id="element-477" data-mp="322vxume5a1wg">
          <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-478" data-mp="2werdxcu5pia2" />
          Reset filters
        </Button>
      </div>
    </div>;
}